import { toast } from 'sonner'
import { ApiError, errorMessage } from '@/lib/api'
import { LIMIT_LABELS } from '@/lib/format'
import { useUpgradePrompt } from '@/stores/upgrade'
import type { LimitKey } from '@/types/api'

const FEATURE_LABELS: Record<string, string> = {
  custom_roles: 'Custom roles',
  advanced_scheduling: 'Advanced scheduling',
  campaigns: 'Campaigns',
  channels: 'Channels',
  api_access: 'API access',
  proof_of_play: 'Proof of play',
  screenshots: 'Remote screenshots',
  data_sources: 'Data sources',
  emergency_broadcasts: 'Emergency broadcasts',
  custom_branding: 'Custom branding',
  approval_workflow: 'Approval workflow',
  flows: 'Signetix Flow automations',
}

export function featureLabel(key: string): string {
  return FEATURE_LABELS[key] ?? key.replace(/_/g, ' ')
}

/**
 * Opens the upgrade prompt for plan-related problems.
 * @returns true when handled.
 */
export function handlePlanError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false
  if (error.type === 'quota-exceeded') {
    const key = typeof error.problem.quota === 'string' ? error.problem.quota : null
    const label = key && key in LIMIT_LABELS ? LIMIT_LABELS[key as LimitKey] : null
    useUpgradePrompt.getState().show({
      reason: 'quota',
      title: label ? `${label} limit reached` : undefined,
      detail: error.detail,
      key,
      limit: typeof error.problem.limit === 'number' ? error.problem.limit : null,
    })
    return true
  }
  if (error.type === 'feature-unavailable') {
    const key = typeof error.problem.feature === 'string' ? error.problem.feature : null
    useUpgradePrompt.getState().show({
      reason: 'feature',
      title: key ? `${featureLabel(key)} isn’t included in your plan` : undefined,
      detail: error.detail,
      key,
    })
    return true
  }
  return false
}

/** Default mutation error UX: plan prompts → upgrade dialog, everything else → toast. */
export function notifyError(error: unknown, fallback = 'Something went wrong') {
  if (handlePlanError(error)) return
  toast.error(error instanceof ApiError ? error.title : fallback, { description: errorMessage(error, fallback) })
}
